import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { isAxiosError } from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { trainingApi } from '@/entities/Training';
import type { MemberTrainingStatus, TrainingDetail } from '@/entities/Training';
import { clearBreadcrumbLabel, setBreadcrumbLabel, useCustomToast } from '@/shared/lib';
import { useChangeCollaboratorStatus } from './useChangeCollaboratorStatus';
import { resolveViewTrainingPageTitle } from '../lib/resolveViewTrainingPageTitle';

export const HOME_ROUTE_NAME = 'Home';

export function useViewTraining() {
  const route = useRoute();
  const router = useRouter();
  const toast = useCustomToast();

  const loading = ref(true);
  const training = ref<TrainingDetail | null>(null);
  const changingTraineeStatusIds = ref<Set<number>>(new Set());
  const { changeStatus: performChangeStatus } = useChangeCollaboratorStatus();
  const pageTitle = computed(() =>
    training.value ? resolveViewTrainingPageTitle(training.value) : ''
  );
  let currentRequestId = 0;

  function redirectToHome(): void {
    router.push({ name: HOME_ROUTE_NAME });
  }

  function failWith(title: string, message: string): void {
    toast.showErrorToast({
      title,
      message,
      type: 'error',
    });
    redirectToHome();
  }

  async function fetchTraining(): Promise<void> {
    const requestId = ++currentRequestId;
    training.value = null;
    const trainingId = Number(route.params.id);
    if (!Number.isInteger(trainingId) || trainingId <= 0) {
      if (requestId === currentRequestId) {
        loading.value = false;
      }
      failWith('Treinamento não encontrado', 'O treinamento informado não é válido.');
      return;
    }

    loading.value = true;
    try {
      const result = await trainingApi.getTrainingById(trainingId);
      if (requestId !== currentRequestId) return;

      training.value = result;
      setBreadcrumbLabel(pageTitle.value);
    } catch (error) {
      if (requestId !== currentRequestId) return;

      training.value = null;
      if (isAxiosError(error) && error.response?.status === 404) {
        failWith(
          'Treinamento não encontrado',
          'Não foi possível carregar o treinamento. Ele pode ter sido excluído.'
        );
      } else {
        failWith(
          'Erro ao carregar treinamento',
          'Não foi possível carregar o treinamento. Tente novamente mais tarde.'
        );
      }
    } finally {
      if (requestId === currentRequestId) {
        loading.value = false;
      }
    }
  }

  function isChangingTraineeStatus(id: number): boolean {
    return changingTraineeStatusIds.value.has(id);
  }

  async function changeTraineeStatus(id: number, newStatus: MemberTrainingStatus): Promise<void> {
    const current = training.value;
    if (!current || changingTraineeStatusIds.value.has(id)) return;

    changingTraineeStatusIds.value = new Set(changingTraineeStatusIds.value).add(id);
    try {
      const updated = await performChangeStatus(current.id, id, newStatus);
      if (updated && training.value) {
        training.value = {
          ...training.value,
          trainees: training.value.trainees.map((trainee) =>
            trainee.id === id ? { ...trainee, status: updated.status } : trainee
          ),
        };
      }
    } finally {
      const next = new Set(changingTraineeStatusIds.value);
      next.delete(id);
      changingTraineeStatusIds.value = next;
    }
  }

  /**
   * Replaces the training on screen with one already fetched elsewhere, so an
   * action that returns the updated detail refreshes the view without a refetch.
   */
  function applyTraining(updated: TrainingDetail): void {
    training.value = updated;
    setBreadcrumbLabel(resolveViewTrainingPageTitle(updated));
  }

  onBeforeUnmount(() => {
    clearBreadcrumbLabel();
  });

  watch(
    () => route.params.id,
    () => void fetchTraining(),
    { immediate: true }
  );

  return {
    loading,
    training,
    changeTraineeStatus,
    isChangingTraineeStatus,
    pageTitle,
    refresh: fetchTraining,
    applyTraining,
  };
}
